import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ProgressButton from 'components/ProgressButton';
import { Trans } from '@lingui/react';
import React from 'react';
import {
  CustomReportFieldType,
  CustomReportRequirementStatusDto,
  CustomReportSubmissionStatus,
  CustomReportTargetType,
  FillType,
  useClientQuery,
  useCustomReportResolvedValuesQuery,
  useCustomReportTemplateFileQuery,
  usePoursQuery,
  useProjectQuery,
  useSaveCustomReportSubmissionMutation
} from 'providers/api';
import { useScenario } from 'modules/project/providers/scenario';
import useStopeRoutePoints from 'modules/project/StopeNotePage/useStopeRoutePoints';
import CustomReportExcelTemplateEditor, {
  buildCustomReportExcelTemplatePdfBlob,
  buildCustomReportExcelTemplateWorkbookBlob,
  CustomReportExcelFormValues,
  findCustomReportExcelTemplate,
  mergeCustomReportExcelFormValues
} from './CustomReportExcelTemplateEditor';
import { CustomReportFormioRenderer } from './CustomReportFormioEditor';
import CustomReportPdfAnnotationEditor from './CustomReportPdfAnnotationEditor';
import {
  collectCustomReportFormComponents,
  customReportFieldsFromFormSchema,
  customReportValuesFromFormData,
  formDataFromCustomReportValues,
  hasCustomReportFormInputs
} from './customReportFormSchema';
import {
  PDF_ANNOTATIONS_VALUE_KEY,
  parsePdfAnnotations,
  serialisePdfAnnotations
} from './customReportPdfAnnotations';
import exportCustomReportFormPdf from './customReportFormPdfExport';
import exportFilledCustomReportPdf from './customReportPdfExport';

interface CustomReportDocumentDialogProps {
  open: boolean;
  projectId: string;
  requirement: CustomReportRequirementStatusDto;
  onClose: () => void;
}

const targetTypeLabel = (targetType?: CustomReportTargetType) => (
  targetType === CustomReportTargetType.Pour ? 'Pour' : 'Stope'
);

const mineSmartPrintLogo = `${import.meta.env.BASE_URL}BackfillPro_HORIZONTAL_LOGO_BLACK.png`;

// How wide the form is while filling it in.
const FILL_PAGE_WIDTH = 1280;
// ...and how wide it is for the split second it's captured to PDF. These must stay separate: the exporter
// scales the captured element down to A4's content width (523pt), so printed text size works out to
// `cssFontPx * 523 / captureWidth`. Capturing at the on-screen width would print 14px labels at ~5.7pt;
// capturing at roughly A4's own content width keeps them near 10pt however wide the screen form gets.
const PRINT_CAPTURE_WIDTH = 700;

// Excel-authored reports store the filled sheet values (per submission) under this key.
const EXCEL_VALUES_KEY = '__customReportExcelValues';

const parseExcelFormValues = (values?: Record<string, string>): CustomReportExcelFormValues | undefined => {
  const raw = values?.[EXCEL_VALUES_KEY];
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as CustomReportExcelFormValues;
  } catch {
    return undefined;
  }
};

const CustomReportDocumentDialog = ({
  open,
  projectId,
  requirement,
  onClose,
}: CustomReportDocumentDialogProps) => {
  const initialValues: Record<string, string> = requirement.submission?.values ?? {};
  const [annotations, setAnnotations] = React.useState(parsePdfAnnotations(initialValues));
  const [formData, setFormData] = React.useState<Record<string, unknown>>(formDataFromCustomReportValues(initialValues));
  const [isExportingPdf, setIsExportingPdf] = React.useState(false);
  // True only while the PDF is being captured — swaps the form to white print styling for the snapshot.
  const [isPrintCapture, setIsPrintCapture] = React.useState(false);
  const [exportError, setExportError] = React.useState(false);
  const [isFormRendererReady, setIsFormRendererReady] = React.useState(false);
  const formBuilderExportRef = React.useRef<HTMLDivElement | null>(null);
  const saveMutation = useSaveCustomReportSubmissionMutation(projectId);
  const { data: project } = useProjectQuery(projectId, { enabled: open });
  const clientId = project?.client.entityId ?? '';
  const { data: client } = useClientQuery(clientId, { enabled: open && Boolean(clientId) });
  const defaultLogoDataUri = client?.logoImageBase64 || mineSmartPrintLogo;
  const formSchema = requirement.template?.definition?.formSchema;
  const isFormBuilderTemplate = hasCustomReportFormInputs(formSchema);
  const formFields = customReportFieldsFromFormSchema(formSchema);
  // Reports authored from an Excel template should be filled as the original spreadsheet form,
  // not the flattened PDF that was generated for download/preview.
  const excelTemplate = React.useMemo(
    () => findCustomReportExcelTemplate(requirement.template?.sourceFile?.display)
      ?? findCustomReportExcelTemplate(requirement.template?.name),
    [requirement.template?.name, requirement.template?.sourceFile?.display],
  );
  const isExcelTemplate = Boolean(excelTemplate && requirement.template?.definition?.excelFormValues && !isFormBuilderTemplate);
  const normalizedTemplateExcelFormValues: CustomReportExcelFormValues | undefined = React.useMemo(() => {
    const src = requirement.template?.definition?.excelFormValues as any;
    if (!src) return undefined;
    return {
      ...src,
      cells: src.cells ?? {},
      richCells: src.richCells ?? {},
    } as CustomReportExcelFormValues;
  }, [requirement.template?.definition?.excelFormValues]);

  const [excelFormValues, setExcelFormValues] = React.useState<CustomReportExcelFormValues>(
    () => mergeCustomReportExcelFormValues(
      excelTemplate,
      parseExcelFormValues(initialValues) ?? normalizedTemplateExcelFormValues,
    ),
  );
  const isPdfTemplate = !isExcelTemplate && Boolean(requirement.template?.sourceFile?.display?.toLowerCase().endsWith('.pdf'));
  const { data: pdfBlob, isLoading: isPdfLoading } = useCustomReportTemplateFileQuery(
    { projectId, templateId: requirement.template?.entityId ?? '' },
    { enabled: open && isPdfTemplate },
  );
  const isComplete = requirement.submission?.status === CustomReportSubmissionStatus.Completed || requirement.isComplete;

  // For a Stope target, compute route points the same way the native stope note does (headless
  // reticulation model, client-side) so route-derived figures (pipeline length, static difference,
  // per-pour friction loss / time-to-stope) resolve. Only for form-builder stope reports.
  const isStopeTarget = requirement.targetType === CustomReportTargetType.Stope;
  const routeComputationEnabled = open && isFormBuilderTemplate && isStopeTarget;
  const { activeScenarioId } = useScenario();
  const { data: stopePours = [] } = usePoursQuery(
    {
      projectId,
      stopeId: requirement.targetId ?? '',
      scenarioId: activeScenarioId ?? undefined,
      includeRockFill: true,
    },
    { enabled: routeComputationEnabled },
  );
  const { routePointsByPour } = useStopeRoutePoints({
    projectId,
    stopeId: requirement.targetId ?? '',
    pours: stopePours,
    fillType: (project?.fillType ?? FillType.Paste) as FillType,
    enabled: routeComputationEnabled && Boolean(project),
  });
  // Serialised so it is a stable primitive in the query key: empty until routes finish computing, then
  // the query refetches with them and the route-derived figures fill in (non-blocking).
  const routePointsJson = React.useMemo(
    () => (Object.keys(routePointsByPour).length ? JSON.stringify(routePointsByPour) : undefined),
    [routePointsByPour],
  );

  // Resolve this target's real data so bound fields auto-fill when the report is generated. Only needed
  // for form-builder templates; the resolver returns bindingKey -> value (and a JSON Pours collection).
  const { data: resolvedValues } = useCustomReportResolvedValuesQuery(
    {
      projectId,
      templateId: requirement.template?.entityId ?? '',
      targetType: requirement.targetType ?? CustomReportTargetType.Pour,
      targetId: requirement.targetId ?? '',
      includeRoutePoints: Boolean(routePointsJson),
      routePoints: routePointsJson,
    },
    { enabled: open && isFormBuilderTemplate },
  );

  // Single-value bindings: component.key -> binding key, excluding repeating tables (handled separately).
  const boundKeyByComponentKey = React.useMemo(() => {
    const map: Record<string, string> = {};
    collectCustomReportFormComponents(formSchema?.components).forEach((component) => {
      if (component.key && typeof component.customReportBinding === 'string' && component.type !== 'datagrid') {
        map[component.key] = component.customReportBinding;
      }
    });
    return map;
  }, [formSchema]);

  // Collection bindings: datagrid.key -> binding key (e.g. 'stope.pours'), whose rows come as a JSON array.
  const collectionByComponentKey = React.useMemo(() => {
    const map: Record<string, string> = {};
    collectCustomReportFormComponents(formSchema?.components).forEach((component) => {
      if (component.key && typeof component.customReportBinding === 'string' && component.type === 'datagrid') {
        map[component.key] = component.customReportBinding;
      }
    });
    return map;
  }, [formSchema]);

  React.useEffect(() => {
    if (open) {
      setAnnotations(parsePdfAnnotations(requirement.submission?.values));
      setFormData(formDataFromCustomReportValues(requirement.submission?.values));
      setExcelFormValues(mergeCustomReportExcelFormValues(
        excelTemplate,
        parseExcelFormValues(requirement.submission?.values) ?? normalizedTemplateExcelFormValues,
      ));
      setExportError(false);
      setIsFormRendererReady(false);
    }
  }, [open, requirement.submission?.entityId, requirement.submission?.status]);

  // Overlay resolved data onto bound fields, but never clobber a value the user has already entered
  // (or one carried on the saved submission). Runs when the resolved data arrives.
  React.useEffect(() => {
    if (!open || !isFormBuilderTemplate || !resolvedValues) return;
    const submissionValues = requirement.submission?.values ?? {};
    setFormData((prev) => {
      const next = { ...prev };
      let changed = false;

      // Single-value fields.
      Object.entries(boundKeyByComponentKey).forEach(([componentKey, bindingKey]) => {
        const resolved = resolvedValues[bindingKey];
        if (resolved === undefined || resolved === '') return;
        const alreadySubmitted = Boolean(submissionValues[componentKey]);
        const alreadyEntered = next[componentKey] !== undefined && next[componentKey] !== '';
        if (!alreadySubmitted && !alreadyEntered) {
          next[componentKey] = resolved;
          changed = true;
        }
      });

      // Repeating tables (datagrids): the resolved value is a JSON array of row objects.
      Object.entries(collectionByComponentKey).forEach(([componentKey, bindingKey]) => {
        const resolved = resolvedValues[bindingKey];
        if (!resolved) return;
        const existing = next[componentKey];
        const alreadyEntered = Array.isArray(existing) && existing.length > 0;
        if (alreadyEntered) return;
        try {
          const rows = JSON.parse(resolved);
          if (Array.isArray(rows)) {
            next[componentKey] = rows;
            changed = true;
          }
        } catch {
          // Leave the table empty if the collection can't be parsed.
        }
      });

      return changed ? next : prev;
    });
  }, [open, isFormBuilderTemplate, resolvedValues, boundKeyByComponentKey, collectionByComponentKey, requirement.submission?.entityId]);

  const buildValues = (): Record<string, string> => {
    if (isExcelTemplate) {
      return {
        ...(requirement.submission?.values ?? {}),
        [EXCEL_VALUES_KEY]: JSON.stringify(excelFormValues),
      };
    }

    if (isFormBuilderTemplate) {
      return customReportValuesFromFormData(formData, formFields);
    }

    const values: Record<string, string> = {
      ...(requirement.submission?.values ?? {}),
      [PDF_ANNOTATIONS_VALUE_KEY]: serialisePdfAnnotations(annotations),
    };

    (requirement.template?.definition?.fields ?? []).forEach((field) => {
      if (!field.id || !field.required || values[field.id ?? '']) return;
      values[field.id ?? ''] = field.type === CustomReportFieldType.Checkbox ? 'true' : 'completed-on-pdf';
    });

    return values;
  };

  const handleSave = () => {
    saveMutation.mutate({
      projectId,
      templateId: requirement.template?.entityId ?? '',
      targetType: requirement.targetType ?? CustomReportTargetType.Pour,
      targetId: requirement.targetId ?? '',
      form: {
        values: buildValues(),
        complete: true,
      },
    }, {
      onSuccess: onClose,
    });
  };

  // Depended on as a plain string below: a new `requirement` object with the same contents must not
  // re-trigger a capture that is already running.
  const reportName = `${requirement.template?.name}-${targetTypeLabel(requirement.targetType ?? CustomReportTargetType.Pour)}-${requirement.targetId ?? ''}`;

  // Runs once `isPrintCapture` has been committed, so the form is already showing the white print styling
  // by the time html2canvas reads it — the PDF stays white paper even though the fill view is dark.
  React.useEffect(() => {
    if (!isPrintCapture) return undefined;

    let cancelled = false;

    const capture = async () => {
      try {
        if (!formBuilderExportRef.current) return;

        await exportCustomReportFormPdf({
          element: formBuilderExportRef.current,
          reportName,
        });
      } catch {
        if (!cancelled) setExportError(true);
      } finally {
        if (!cancelled) {
          setIsPrintCapture(false);
          setIsExportingPdf(false);
        }
      }
    };

    capture();

    return () => {
      cancelled = true;
    };
  }, [isPrintCapture, reportName]);

  const handleDownloadPdf = async () => {
    setExportError(false);
    setIsExportingPdf(true);

    // Form-builder reports are captured from the live form, which is styled dark for filling. Hand off to
    // the print-capture effect below: it swaps in the white print styling first, then clears both flags.
    if (isFormBuilderTemplate) {
      setIsPrintCapture(true);
      return;
    }

    try {
      if (isExcelTemplate && excelTemplate) {
        const excelPdfBlob = await buildCustomReportExcelTemplatePdfBlob(excelTemplate, excelFormValues, defaultLogoDataUri);
        const url = window.URL.createObjectURL(excelPdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${reportName.replace(/[^a-z0-9]+/gi, '-')}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        return;
      }

      if (!isPdfTemplate || !pdfBlob) return;

      await exportFilledCustomReportPdf({
        pdfBlob,
        reportName,
        fields: [],
        values: buildValues(),
      });
    } catch {
      setExportError(true);
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!isExcelTemplate || !excelTemplate) return;
    const blob = buildCustomReportExcelTemplateWorkbookBlob(excelTemplate, excelFormValues);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportName.replace(/[^a-z0-9]+/gi, '-')}.xls`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const canDownloadPdf = isPdfTemplate || isFormBuilderTemplate || isExcelTemplate;
  let isDownloadPdfDisabled = !isFormRendererReady;
  if (isExcelTemplate) {
    isDownloadPdfDisabled = false;
  } else if (isPdfTemplate) {
    isDownloadPdfDisabled = isPdfLoading || !pdfBlob;
  }
  let isSaveDisabled: boolean;
  if (isExcelTemplate) {
    isSaveDisabled = false;
  } else if (isPdfTemplate) {
    isSaveDisabled = isPdfLoading || !pdfBlob;
  } else {
    isSaveDisabled = !isFormBuilderTemplate;
  }

  let editorContent: JSX.Element;
  if (isExcelTemplate && excelTemplate) {
    editorContent = (
      <CustomReportExcelTemplateEditor
        template={excelTemplate}
        values={excelFormValues}
        onChange={setExcelFormValues}
        defaultLogoDataUri={defaultLogoDataUri}
      />
    );
  } else if (isFormBuilderTemplate) {
    editorContent = (
      // Centred page column, and also the capture element — it narrows to the print width for the capture
      // so the on-screen width never shrinks the text in the exported PDF.
      <Box
        ref={formBuilderExportRef}
        sx={{
          maxWidth: isPrintCapture ? PRINT_CAPTURE_WIDTH : FILL_PAGE_WIDTH,
          mx: 'auto',
          width: '100%',
        }}
      >
        <CustomReportFormioRenderer
          schema={formSchema}
          values={formData}
          onChange={setFormData}
          defaultLogoDataUri={defaultLogoDataUri}
          onReadyChange={setIsFormRendererReady}
          printMode={isPrintCapture}
        />
      </Box>
    );
  } else {
    editorContent = (
      <CustomReportPdfAnnotationEditor
        pdfBlob={pdfBlob}
        isLoading={isPdfLoading}
        annotations={annotations}
        onAnnotationsChange={setAnnotations}
        maxWidth={1040}
        defaultLogoDataUri={defaultLogoDataUri}
      />
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>{requirement.template?.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {`${targetTypeLabel(requirement.targetType ?? CustomReportTargetType.Pour)} ${requirement.targetId ?? ''}`}
          </Typography>
        </Box>
        <Chip
          icon={isComplete ? <CheckCircleIcon /> : <WarningAmberIcon />}
          color={isComplete ? 'success' : 'warning'}
          label={isComplete ? 'Saved' : 'Required'}
          variant="outlined"
        />
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: isFormBuilderTemplate ? 'background.default' : '#303030', p: { xs: 1, md: 2 } }}>
        {!isPdfTemplate && !isFormBuilderTemplate && !isExcelTemplate && (
          <Alert severity="warning">
            <Trans id="This report does not have a PDF form attached.">This report does not have a PDF form attached.</Trans>
          </Alert>
        )}
        {exportError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            <Trans id="Unable to export the filled PDF.">Unable to export the filled PDF.</Trans>
          </Alert>
        )}
        {editorContent}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', px: 2, py: 1.25 }}>
        {isExcelTemplate && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadExcel}
          >
            <Trans id="Download Excel">Download Excel</Trans>
          </Button>
        )}
        {canDownloadPdf && (
          <ProgressButton
            variant="outlined"
            color="secondary"
            loading={isExportingPdf}
            disabled={isDownloadPdfDisabled}
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPdf}
          >
            <Trans id="Download PDF">Download PDF</Trans>
          </ProgressButton>
        )}
        <Button onClick={onClose}><Trans id="Cancel">Cancel</Trans></Button>
        <ProgressButton
          variant="contained"
          color="secondary"
          loading={saveMutation.isLoading}
          disabled={isSaveDisabled}
          onClick={handleSave}
        >
          <Trans id="Save">Save</Trans>
        </ProgressButton>
      </DialogActions>
    </Dialog>
  );
};

export default CustomReportDocumentDialog;
